// «Мой пул» — the admin's working screen. Tiles + alert strips + the table.
// Managers additionally see the owner column; layout agreed with Tim 02.08.
import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  api,
  ApiError,
  type DatoricAddContributorsRes as AddResult,
  type DatoricStaffMe as Me,
  type DatoricPoolRow as PoolRow,
  type DatoricSummaryRes as Summary,
} from '../api';

/** Domain errors of the rate cascade, translated once for every call site. */
export const rateErrorText = (e: unknown): string | null => {
  if (!(e instanceof ApiError)) return null;
  if (e.code === 'no_admin_rate') {
    return 'Ставки контрибьюторам станут доступны, когда менеджер назначит твою ставку.';
  }
  if (e.code === 'rate_above_admin') {
    const max = typeof e.body?.maxCents === 'number' ? ` (сейчас $${(e.body.maxCents / 100).toFixed(2)}/ч)` : '';
    return `Ставка контрибьютора должна быть ниже твоей${max}.`;
  }
  if (e.code === 'rate_below_pool') {
    const max = typeof e.body?.maxCents === 'number' ? ` $${(e.body.maxCents / 100).toFixed(2)}/ч` : '';
    return `В пуле этого админа уже есть ставка${max} — ставка админа должна оставаться выше. Понизить её получится после снижения ставок контрибьюторов.`;
  }
  return null;
};

const fmtH = (h: number): string => h.toFixed(h >= 100 ? 0 : 1);
const fmtMoney = (cents: number | null): string =>
  cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;
/** Дата и время — как просили админы: по «5 дн назад» не сказать, когда именно
 *  человек появился, а им это нужно, чтобы отличать новичка от давно молчащего.
 *  Год не сокращаем: набор идёт второй год, и «21.08» без года двусмысленно. */
const stamp = (ms: number | null): string =>
  ms
    ? new Date(ms).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const ago = (ms: number | null): string => {
  if (!ms) return '—';
  const d = Math.floor((Date.now() - ms) / 86_400_000);
  if (d <= 0) return 'сегодня';
  if (d === 1) return 'вчера';
  return `${d} дн назад`;
};

const STATUS_LABEL: Record<PoolRow['status'], string> = {
  invited: 'приглашён',
  joined: 'вошёл',
  active: 'активен',
  blocked: 'заблокирован',
};

export function PoolPage({ me }: { me: Me }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<PoolRow[] | null>(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rateErr, setRateErr] = useState<string | null>(null);
  const [payErr, setPayErr] = useState<string | null>(null);
  const [payBusy, setPayBusy] = useState(false);
  // The manager screen links here with #/?owner=<name> to show one admin's pool.
  const [ownerFilter, setOwnerFilter] = useState<string>(
    () => new URLSearchParams(location.hash.split('?')[1] ?? '').get('owner') ?? '',
  );

  const load = () => {
    api.summary().then(setSummary).catch(() => {});
    api.pool().then((r) => setRows(r.rows)).catch(() => setRows([]));
  };
  useEffect(load, []);

  // Tab clicks change the hash without remounting this page — the filter must
  // follow the URL, not survive it.
  useEffect(() => {
    const on = () =>
      setOwnerFilter(new URLSearchParams(location.hash.split('?')[1] ?? '').get('owner') ?? '');
    addEventListener('hashchange', on);
    return () => removeEventListener('hashchange', on);
  }, []);

  const view = useMemo(() => {
    if (!rows) return null;
    const needle = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!needle || r.email.includes(needle) || (r.name ?? '').toLowerCase().includes(needle)) &&
        (!statusFilter || r.status === statusFilter) &&
        // By id, not name: two admins can share a name, their pools must not merge.
        (!ownerFilter || r.ownerId === ownerFilter),
    );
  }, [rows, q, statusFilter, ownerFilter]);

  // Inline rate edit: blur or Enter commits, empty clears. The backend PATCH is
  // scoped, so a viewer gets a 403 and the cell simply reverts on reload.
  /** СПОСОБ 1 (как просил Иван): менеджер вписывает накопленную сумму прямо в
   *  строке. В реестр уходит разница — поле привычное, история цела. */
  const savePaid = async (row: PoolRow, raw: string) => {
    const dollars = raw.trim() === '' ? 0 : Number(raw.replace(',', '.'));
    if (!Number.isFinite(dollars) || dollars < 0) return;
    const paidCents = Math.round(dollars * 100);
    if (paidCents === row.paidCents) return;
    try {
      await api.setPaid(row.id, paidCents);
      setPayErr(null);
      setRows(
        (rs) =>
          rs?.map((r) =>
            r.id === row.id
              ? { ...r, paidCents, debtCents: r.earnedCents == null ? null : r.earnedCents - paidCents }
              : r,
          ) ?? null,
      );
    } catch {
      setPayErr('Не удалось записать сумму.');
      load();
    }
  };

  /** СПОСОБ 2 (предложение Тима): одной кнопкой закрыть долг всем, кому должны.
   *  Платят пачкой за период, обходить полтора десятка карточек — та же работа,
   *  только дольше и с шансом кого-то пропустить. */
  const payEveryone = async () => {
    const owing = (rows ?? []).filter((r) => (r.debtCents ?? 0) > 0);
    const total = owing.reduce((sum, r) => sum + (r.debtCents ?? 0), 0);
    if (owing.length === 0) return;
    if (!confirm(`Записать выплату ${owing.length} чел. на $${(total / 100).toFixed(2)}?`)) return;
    setPayBusy(true);
    setPayErr(null);
    try {
      const res = await api.payBulk();
      setPayErr(`Записано: ${res.people} чел., $${(res.totalCents / 100).toFixed(2)}.`);
      load();
    } catch {
      setPayErr('Не удалось записать выплату пачкой.');
    } finally {
      setPayBusy(false);
    }
  };

  const saveRate = async (row: PoolRow, raw: string) => {
    const dollars = raw.trim() === '' ? null : Number(raw);
    if (dollars !== null && (!Number.isFinite(dollars) || dollars < 0)) return;
    const rateCents = dollars === null ? null : Math.round(dollars * 100);
    if (rateCents === row.rateCents) return;
    try {
      await api.patchContributor(row.id, { rateCents });
      setRateErr(null);
      setRows((rs) => rs?.map((r) => (r.id === row.id ? { ...r, rateCents } : r)) ?? null);
    } catch (e) {
      setRateErr(rateErrorText(e));
      load();
    }
  };

  return (
    <>
      {summary && (
        <>
          <div class="tiles">
            <div class="tile"><div class="n">{fmtH(summary.acceptedHours)} ч</div><div class="t">принято</div></div>
            <div class="tile"><div class="n">{fmtH(summary.pendingHours)} ч</div><div class="t">на проверке</div></div>
            <div class="tile"><div class="n">{fmtH(summary.rejectedHours)} ч</div><div class="t">брак</div></div>
            {summary.payoutCents != null && (
              <div class="tile"><div class="n">{fmtMoney(summary.payoutCents)}</div><div class="t">доход</div></div>
            )}
            <div class="tile"><div class="n">{summary.matched}/{summary.people}</div><div class="t">активировались</div></div>
          </div>
          {summary.expiring.files > 0 && (
            <div class="strip warn">
              ⚠ {fmtH(summary.expiring.hours)} ч ({summary.expiring.files} файл(ов)) на проверке
              дольше 6 дней — завтра истечёт срок проверки, и они уйдут в брак.
            </div>
          )}
          {summary.flagged > 0 && (
            <div class="strip bad">⛔ Анти-фрод: отмечено {summary.flagged} чел.</div>
          )}
        </>
      )}

      {payErr && <div class="strip warn">{payErr}</div>}
      {rateErr && <div class="strip bad">{rateErr}</div>}
      {ownerFilter && (
        <div class="strip info">
          Пул: <b>{rows?.find((r) => r.ownerId === ownerFilter)?.ownerName ?? '…'}</b>{' '}
          <button
            class="link"
            onClick={() => {
              setOwnerFilter('');
              location.hash = '#/';
            }}
          >
            показать всех
          </button>
        </div>
      )}

      <div class="toolbar">
        <button class="primary" onClick={() => setDialogOpen(true)}>+ Добавить людей</button>
        {me.role === 'manager' && (
          <button
            type="button"
            disabled={payBusy || !(rows ?? []).some((r) => (r.debtCents ?? 0) > 0)}
            onClick={() => void payEveryone()}
          >
            {payBusy ? 'Записываем…' : 'Закрыть долг всем'}
          </button>
        )}
        <input
          type="search"
          placeholder="Поиск по email или имени…"
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter((e.target as HTMLSelectElement).value)}>
          <option value="">Все статусы</option>
          <option value="invited">Приглашён</option>
          <option value="joined">Вошёл</option>
          <option value="active">Активен</option>
          <option value="blocked">Заблокирован</option>
        </select>
      </div>

      <div class="tablewrap">
        {view == null ? (
          <div class="empty">Загружаем…</div>
        ) : view.length === 0 ? (
          <div class="empty">
            {rows?.length === 0
              ? 'В пуле пока никого — новых людей можно добавить кнопкой сверху.'
              : 'Никто не подходит под фильтр.'}
          </div>
        ) : (
          <table class="pool-table">
            <thead>
              <tr>
                <th>Контрибьютор</th>
                <th>Статус</th>
                <th>Анти-фрод</th>
                {me.role === 'manager' && <th>Владелец</th>}
                <th>Принято ч</th>
                <th>Ожидает ч</th>
                <th>Брак ч</th>
                <th>Качество</th>
                <th>Ставка $/ч</th>
                <th>Заработал</th>
                <th>Факт оплаты</th>
                <th>Задолженность</th>
                <th>Регистрация</th>
                <th>Активность</th>
                {me.role === 'manager' && me.showBalance && <th>Доход</th>}
              </tr>
            </thead>
            <tbody>
              {view.map((r) => (
                <tr class="row" key={r.id} onClick={() => (location.hash = `#/contributor/${r.id}`)}>
                  <td>
                    {r.email}
                    {r.name && <span class="sub">{r.name}</span>}
                  </td>
                  <td><span class={`chip ${r.status}`}>{STATUS_LABEL[r.status]}</span></td>
                  <td>
                    {r.fraud || r.providerStatus === 'flagged' || r.providerStatus === 'disabled' ? (
                      <span class="chip fraud">{r.fraud ?? r.providerStatus}</span>
                    ) : (
                      <span class="muted">—</span>
                    )}
                  </td>
                  {me.role === 'manager' && <td>{r.ownerName ?? <span class="muted">—</span>}</td>}
                  <td>{fmtH(r.acceptedHours)}</td>
                  <td>{r.pendingHours > 0 ? <span class="chip pend">{fmtH(r.pendingHours)}</span> : '0'}</td>
                  <td>
                    {fmtH(r.rejectedHours)}
                    {r.uploadedHours > 0 && (
                      <span class="sub">{Math.round((r.rejectedHours / r.uploadedHours) * 100)}%</span>
                    )}
                  </td>
                  <td>{r.qualityScore ?? '—'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      class="rate-input"
                      type="number"
                      min="0"
                      step="0.5"
                      value={r.rateCents == null ? '' : (r.rateCents / 100).toString()}
                      placeholder="—"
                      onChange={(e) => saveRate(r, (e.target as HTMLInputElement).value)}
                    />
                  </td>
                  {/* Earned = the admin's own rate promise to this person. Shown
                      only when a rate is set — a guessed default would look like
                      an obligation we never made. */}
                  <td>{r.earnedCents == null ? '—' : fmtMoney(r.earnedCents)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {me.role === 'manager' ? (
                      <input
                        class="rate-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={r.paidCents === 0 ? '' : (r.paidCents / 100).toString()}
                        placeholder="—"
                        onChange={(e) => savePaid(r, (e.target as HTMLInputElement).value)}
                      />
                    ) : r.paidCents > 0 ? (
                      fmtMoney(r.paidCents)
                    ) : (
                      <span class="muted">—</span>
                    )}
                  </td>
                  {/* Долг подсвечиваем только когда он есть: ноль — это норма,
                      а не достижение, и красить его не за что. */}
                  <td class={r.debtCents != null && r.debtCents > 0 ? 'owed' : undefined}>
                    {r.debtCents == null ? '—' : fmtMoney(r.debtCents)}
                  </td>
                  <td>{stamp(r.createdAt)}</td>
                  <td>{ago(r.lastActiveAt)}</td>
                  {me.role === 'manager' && me.showBalance && <td>{fmtMoney(r.payoutCents)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dialogOpen && <AddDialog onClose={() => setDialogOpen(false)} onDone={load} />}
    </>
  );
}

// ── one-time cabinet credentials from the add batch ─────────────────────────

function AccountsList({ accounts }: { accounts: Array<{ email: string; password: string }> }) {
  const [copied, setCopied] = useState(false);
  // «логин пароль» строками — формат под пересылку списком (Тим, 08.08).
  const text = accounts.map((a) => `${a.email} ${a.password}`).join('\n');
  return (
    <div class="creds">
      <pre>{text}</pre>
      <div class="creds-actions">
        <button
          type="button"
          class="primary"
          onClick={() => navigator.clipboard.writeText(text).then(() => setCopied(true))}
        >
          {copied ? '✓ Скопировано' : `Скопировать все (${accounts.length})`}
        </button>
      </div>
      <div class="muted">
        Доступы в кабинет datoric.weern.com. Показываются один раз — дальше только смена пароля на
        карточке.
      </div>
    </div>
  );
}

// ── add-people dialog ────────────────────────────────────────────────────────

function AddDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  // The dialog must be opened with showModal(), not the `open` attribute: with
  // the attribute it renders non-modal — pinned to a corner, no backdrop,
  // clicks falling through to the table underneath.
  //
  // Opened from a callback ref rather than useEffect: the effect ran before
  // Preact had attached the ref, so the dialog stayed closed. The `!el.open`
  // guard matters because a callback ref fires on re-renders too, and
  // showModal() on an already-open dialog throws InvalidStateError.
  const openModal = (el: HTMLDialogElement | null) => {
    if (el && !el.open) el.showModal();
  };
  const [emailsRaw, setEmailsRaw] = useState('');
  const [rateRaw, setRateRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AddResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const emails = emailsRaw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.includes('@'));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const rate = rateRaw.trim() === '' ? undefined : Math.round(Number(rateRaw) * 100);
      const r = await api.addContributors({
        emails: emails.slice(0, 25),
        ...(rate != null && Number.isFinite(rate) ? { rateCents: rate } : {}),
      });
      setResult(r);
      onDone();
    } catch (e) {
      setError(rateErrorText(e) ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  // Reporting the roster push honestly: «в очереди» and «не дошло» are different
  // situations upstream (202/503 vs error) and the admin must know which one
  // they are in — see the Datoric API notes in the worker's CLAUDE.md.
  const upstreamText = (r: AddResult): string => {
    if (r.upstreamError) return '⚠ Доставка не подтверждена — можно повторить позже, люди сохранены у нас.';
    if (!r.upstream) return '';
    if (r.upstream.httpStatus === 201) return `✓ Отправлено в Datoric (${r.upstream.rosterName ?? 'волна создана'}).`;
    if (r.upstream.httpStatus === 202) return '… Datoric поставил в очередь — проверь статус через час.';
    return `⚠ Datoric ответил ${r.upstream.httpStatus} — сохранено у нас, доставка не подтверждена.`;
  };

  return (
    <dialog ref={openModal} onClose={onClose}>
      <h3>Добавить контрибьюторов</h3>
      <label>Email'ы (до 25, через запятую или с новой строки)</label>
      <textarea
        value={emailsRaw}
        onInput={(e) => setEmailsRaw((e.target as HTMLTextAreaElement).value)}
        placeholder={'one@example.com\ntwo@example.com'}
      />
      <label>Ставка, $/час принятого (можно оставить пустым и задать позже)</label>
      <input
        type="number"
        min="0"
        step="0.5"
        value={rateRaw}
        onInput={(e) => setRateRaw((e.target as HTMLInputElement).value)}
        placeholder="например 6"
      />
      {emails.length > 25 && (
        <div class="result">⚠ Больше 25 — уйдут только первые 25, остальные добавятся второй пачкой.</div>
      )}
      {result && (
        <div class="result">
          Добавлено новых: {result.created.length}, уже были: {result.existing.length}.<br />
          {upstreamText(result)}
        </div>
      )}
      {result && result.accounts.length > 0 && (
        <AccountsList accounts={result.accounts} />
      )}
      {error && <div class="result">Ошибка: {error}</div>}
      <div class="actions">
        <button class="ghost" onClick={onClose}>{result ? 'Закрыть' : 'Отмена'}</button>
        {!result && (
          <button class="primary" disabled={busy || emails.length === 0} onClick={submit}>
            {busy ? 'Добавляем…' : `Добавить (${Math.min(emails.length, 25)})`}
          </button>
        )}
      </div>
    </dialog>
  );
}
